import 'styled-components';
import { useContext } from 'react';
import type { ThemedStyledProps, CSSProp, StyledComponent } from 'styled-components';
import { default as styled, ThemeContext, withTheme, ThemeProvider, css, keyframes, createGlobalStyle } from 'styled-components';
import type { Theme } from '../themes/types';

export type { FlattenInterpolation, FlattenSimpleInterpolation, CSSObject, StyledComponent, CSSProp } from 'styled-components';

// const {
//   default: styled,
//   css,
//   createGlobalStyle,
//   keyframes,
//   ThemeProvider,
//   withTheme: wt
// } = styledComponents as styledComponents.ThemedStyledComponentsModule<Theme>;

declare module 'styled-components' {
  interface DefaultTheme extends Theme {}

  // Makes the props (custom props, theme props, component props) readonly by default, automatically
  export interface ThemedStyledFunctionBase<
    C extends keyof JSX.IntrinsicElements | React.ComponentType<any>,
    T extends object,
    O extends object = {},
    A extends keyof any = never,
  > {
    (first: TemplateStringsArray): StyledComponent<C, T, Readonly<O>, A>;
    (
      first: TemplateStringsArray | CSSObject | InterpolationFunction<ThemedStyledProps<StyledComponentPropsWithRef<C> & Readonly<O>, T>>,
      ...rest: Array<Interpolation<ThemedStyledProps<StyledComponentPropsWithRef<C> & Readonly<O>, T>>>
    ): StyledComponent<C, T, Readonly<O>, A>;
    <U extends object>(
      first: TemplateStringsArray | CSSObject | InterpolationFunction<ThemedStyledProps<StyledComponentPropsWithRef<C> & Readonly<O> & Readonly<U>, T>>,
      ...rest: Array<Interpolation<ThemedStyledProps<StyledComponentPropsWithRef<C> & Readonly<O> & Readonly<U>, T>>>
    ): StyledComponent<C, T, Readonly<O> & Readonly<U>, A>;
  }
}

declare module 'react' {
  interface DOMAttributes<T> {
    css?: CSSProp;
  }
}
declare global {
  namespace JSX {
    interface IntrinsicAttributes {
      css?: CSSProp;
    }
  }
}

export type ThemeStyledProps<T> = ThemedStyledProps<T, Theme>;

/**
 * A HOC factory to get the current theme from a `JoyKitThemeProvider` and pass it to your
 * component as a `theme` prop.
 *
 * Note: Should be used within a `JoyKitThemeProvider` hierarchy.
 *
 * @example
 *
 * ```
 * class MyComponent extends React.Component {
 *    render() {
 *      console.log('Current theme: ', this.props.theme)
 *      // ...
 *    }
 *  }
 * ```
 *
 * export const MyComponentWithTheme = withTheme(MyComponent);
 */

const useTheme = (): Theme => {
  return useContext(ThemeContext);
};

type JoyKitStyledComponent<C extends keyof JSX.IntrinsicElements | React.ComponentType<any>, O extends object = {}, A extends keyof any = never> = StyledComponent<C, Theme, O, A>;

export { styled, css, ThemeProvider, withTheme, useTheme, keyframes, createGlobalStyle };
export type { JoyKitStyledComponent };
